// Volume settings, their persistence and the gain each bus gets.

import { SOURCES, type Settings, type Source } from './types.ts';

export const STORAGE_KEY = 'worldciv.music.v1';

export const DEFAULT_SETTINGS: Settings = {
  master: 0.9,
  music: 0.8,
  effects: 0.8,
  muted: false,
  source: 'orchestra',
};

// A slider moves loudness, not amplitude: gain = position ^ TAPER_EXPONENT, so half way is -12 dB.
const TAPER_EXPONENT = 2;

export type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

export interface BusGains {
  music: number;
  effects: number;
}

export function taper(position: number): number {
  return position ** TAPER_EXPONENT;
}

export function busGains(settings: Settings): BusGains {
  if (settings.muted) {
    return { music: 0, effects: 0 };
  }
  const master = taper(settings.master);
  return { music: master * taper(settings.music), effects: master * taper(settings.effects) };
}

function volume(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return fallback;
  }
  return Math.min(1, Math.max(0, value));
}

function source(value: unknown): Source {
  return SOURCES.find((s) => s === value) ?? DEFAULT_SETTINGS.source;
}

/** Settings from stored text; every bad or missing field falls back to its default on its own. */
export function parseSettings(raw: string | null): Settings {
  if (raw === null) {
    return { ...DEFAULT_SETTINGS };
  }

  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return { ...DEFAULT_SETTINGS };
  }

  const stored = data as Record<string, unknown>;
  return {
    master: volume(stored.master, DEFAULT_SETTINGS.master),
    music: volume(stored.music, DEFAULT_SETTINGS.music),
    effects: volume(stored.effects, DEFAULT_SETTINGS.effects),
    muted: typeof stored.muted === 'boolean' ? stored.muted : DEFAULT_SETTINGS.muted,
    source: source(stored.source),
  };
}

// Browser storage can be missing or throw (private window, blocked site data): treat it as empty.
export function loadSettings(storage: StorageLike | null): Settings {
  try {
    return parseSettings(storage?.getItem(STORAGE_KEY) ?? null);
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(storage: StorageLike | null, settings: Settings): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Not persisted; the session still works.
  }
}
